import { nanoid } from 'nanoid'

const state = {
  title: '',
  handle: '',
  description: '',
  faceType: '',
  materials: '',
  type: '',
  monogramEnabled: false,
  monogramImage: '',
  monogramOverlay: null,
  blueBlocker: {},
  clip: { selected: false, label: 'Add Custom Clip' },
  monogram: { selected: false, value: '', color: 'gold' },
  lens: {
    selected: false,
    variantId: null,
    label: "Add <a href='/products/custom-lenses' target='_blank' class='underline underline-offset-2'>Custom Lens</a> ",
  },
  lensVariants: {},
  lensVariantIds: [],
  modelPhotos: [],
  relatedProducts: [],
  size: [],
  sizeSiblings: [],
  productId: 0,
  variantId: 0,
  variantPreviewId: 0,
  // variants: {2409237: { title: 'blah', images: [1242, 435324, 5342, 43262] }},
  // variantIds: [240937, ...]
  // images: {5234532: "https://...", }
  variants: {},
  variantIds: [],
  images: {},
}

/**
 * GETTERS
 */
const getters = {
  getAddonKeys: (state, getters) => {
    let addons = []
    if (state.monogramEnabled) {
      addons.push('monogram')
    }
    if (state.type === 'Sunglasses') {
      addons.push('lens')
    }
    if (state.type === 'Optical' && state.handle !== 'blue-light-filter-lens') {
      addons.push('blueBlocker')
    }
    let clip = getters.getVariant.clip
    if (clip && clip.available) {
      addons.push('clip')
    }
    if (clip && clip.available) {
      addons.push('clipLens')
    }
    return addons
  },
  getSelectedAddons: (state, getters) => {
    return getters.getAddonKeys
      .map(addonKey => {
        switch (addonKey) {
          case 'blueBlocker':
            return state[addonKey].selected ? state[addonKey] : null
          case 'clip':
            if (state[addonKey].selected) {
              return {
                ...state[addonKey],
                ...{ price: getters.getVariant.clip.price, variantId: getters.getVariant.clip.id },
              }
            }
            return null
          case 'lens':
            if (state[addonKey].selected) {
              return { ...state[addonKey], price: getters.getSelectedLens.price }
            }
            return null
          case 'clipLens':
            if (state['clip'].selected && state['lens'].selected) {
              return { ...state['lens'], price: getters.getSelectedLens.price }
            }
            return null
          case 'monogram':
            if (state.monogram.selected && state.monogram.value) {
              return { ...state.monogram, price: state.monogram.price ?? 0 }
            }
            return null
        }
      })
      .filter(i => i) // Remove null values
  },
  getClipVariant: (state, getters) => getters.getVariant.clip,
  getImages: state => state.images,
  getImage: state => id => state.images[id],
  getPreviewVariant: state => {
    let variantId = state.variantPreviewId ? state.variantPreviewId : state.variantId
    return state.variants[variantId]
  },
  getSelectedLens: state => state.lensVariants[state.lens.variantId],
  getVariant: state => state.variants[state.variantId],
  getVariants: state => state.variantIds.map(id => state.variants[id]),
  getVariantImages: (state, getters) => {
    return getters.getVariantImageIds.map(id => state.images[id])
  },
  getVariantAndModelImages: (state, getters) => {
    let variantImgs = getters.getVariantImages
    let modelImgs = state.modelPhotos.map(x => x.model_photos)
    return variantImgs.concat(modelImgs)
  },
  getAllImages: state => {
    let imgs = Object.values(state.images)
    let modelImgs = state.modelPhotos.map(x => x.model_photos)
    return imgs.concat(modelImgs)
  },
  getVariantImageIds: (state, getters) => {
    if (!getters.getPreviewVariant) {
      return []
    }
    return getters.getPreviewVariant.imageIds
  },
  getVariantTitle: (state, getters) => getters.getPreviewVariant.title,
  getTotalPrice: (state, getters) => {
    let totalPrice = getters.getVariant.price
    getters.getSelectedAddons.forEach(item => {
      totalPrice += item.price ?? 0
    })
    return totalPrice
  },
  getRelatedProducts: state => {
    // if(!state.relatedProducts) return
    // Filter out products without a title (empty admin entries)
    return state.relatedProducts.filter(p => p.title)
  },
}

/**
 * MUTATIONS
 */
const mutations = {
  deselectAllAddons(state) {
    state.clip.selected = false
    state.lens.selected = false
    state.blueBlocker.selected = false
    state.monogram.selected = false
    state.monogram.value = ''
    state.monogram.color = 'gold'
  },
  toggleBlueBlocker(state) {
    state.blueBlocker.selected = !state.blueBlocker.selected
  },
  toggleClip(state) {
    state.clip.selected = !state.clip.selected
  },
  toggleLens(state) {
    state.lens.selected = !state.lens.selected
  },
  toggleMonogram(state) {
    state.monogram.selected = !state.monogram.selected
  },
  setBlueBlocker(state, payload) {
    state.blueBlocker = payload
  },
  setDescription(state, payload) {
    state.description = payload
  },
  setFaceType(state, payload) {
    state.faceType = payload
  },
  setMaterials(state, payload) {
    state.materials = payload
  },
  setHandle(state, handle) {
    state.handle = handle
  },
  setImages(state, images) {
    state.images = images
  },
  setLensSelected(state, payload) {
    state.lens.selected = payload
  },
  setLensVariants(state, payload) {
    state.lensVariants = payload
  },
  setLensVariantId(state, payload) {
    state.lens.variantId = payload
  },
  setLensVariantIds(state, payload) {
    state.lensVariantIds = payload
  },
  setMonogramValue(state, payload) {
    state.monogram.value = payload
  },
  setMonogramColor(state, payload) {
    state.monogram.color = payload
  },
  setModelPhotos(state, payload) {
    state.modelPhotos = payload ? payload : []
  },
  setProductId(state, productId) {
    state.productId = productId
  },
  setRelatedProducts(state, payload) {
    state.relatedProducts = payload ? payload : []
  },
  setSize(state, size) {
    state.size = size
  },
  setSizeSiblings(state, sizeSiblings) {
    state.sizeSiblings = sizeSiblings
  },
  setTitle(state, title) {
    state.title = title
  },
  setType(state, type) {
    state.type = type
  },
  setMonogramEnabled(state, payload) {
    state.monogramEnabled = payload
  },
  setMonogramImage(state, payload) {
    state.monogramImage = payload || ''
  },
  setMonogramOverlay(state, payload) {
    state.monogramOverlay = payload || null
  },
  setVariants(state, variants) {
    state.variants = variants
  },
  setVariantId(state, variantId) {
    state.variantId = variantId
  },
  setVariantPreviewId(state, variantId) {
    state.variantPreviewId = variantId
  },
  setVariantIds(state, variantIds) {
    state.variantIds = variantIds
  },
}

/**
 * ACTIONS
 */
const actions = {
  loadProduct({ commit }, product) {
    let images = {}
    let variants = {}
    let variantIds = []
    let imageId = null
    product.variants.forEach(v => {
      let variant = {
        title: v.title,
        id: v.id,
        imageIds: [],
        price: v.price,
        available: v.available,
        inStock: v.inStock,
      }
      variants[v.id] = variant
      variantIds.push(v.id)
      if (v.featured_image) {
        images[v.featured_image.id] = v.featured_image.src
        variant.imageIds.push(v.featured_image.id)
      }
      v.extraImages.forEach(image => {
        imageId = nanoid()
        images[imageId] = image
        variant.imageIds.push(imageId)
      })
      if (v.clip) {
        variant.clip = v.clip
      }
    })
    commit('setImages', images)
    commit('setVariants', variants)
    commit('setVariantIds', variantIds)
    commit('setProductId', product.id)
    commit('setVariantId', product.variantId)
    commit('setHandle', product.handle)
    commit('setTitle', product.title)
    commit('setType', product.type)
    const isFrameType = product.type === 'Optical' || product.type === 'Sunglasses'
    const monogramEnabled =
      typeof product.monogramEnabled === 'boolean'
        ? product.monogramEnabled
        : isFrameType
    commit('setMonogramEnabled', monogramEnabled)
    commit('setMonogramImage', product.monogramImage)
    commit('setMonogramOverlay', product.monogramOverlay)
    commit('setDescription', product.description)
    commit('setFaceType', product.faceType)
    commit('setMaterials', product.materials)
    commit('setSize', product.size)
    commit('setSizeSiblings', product.sizeSiblings)
    commit('setLensVariants', product.lensVariants)
    commit('setLensVariantIds', product.lensVariantIds)
    commit('setLensVariantId', product.lensVariantIds[0])
    commit('setModelPhotos', product.modelPhotos)
    commit('setRelatedProducts', product.relatedProducts)

    if (product.type === 'Optical') {
      commit('setBlueBlocker', {
        selected: false,
        price: product.blueBlocker.price,
        variantId: product.blueBlocker.variants[0].id,
        title: product.blueBlocker.title,
        label:
          "Add <a href='/products/blue-light-filter-lens' target='_blank' class='underline'>Blue Light Filter Lens</a> ",
      })
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
